export enum Environment {
  Production = 'production',
  Candidate = 'candidate',
  UAT = 'uat',
  EU = 'eu',
  Main = 'main',
  QA1 = 'qa1',
  QA2 = 'qa2',
}

const baseUrls = {
  [Environment.Production]: `https://prostudio.pypestream.com`,
  [Environment.EU]: `https://prostudio.pypestream.eu`,
  [Environment.Candidate]: `https://prostudio.candidate.pypestream.dev`,
  [Environment.Main]: `https://prostudio.main.pypestream.dev`,
  [Environment.UAT]: `https://prostudio.uat.pypestream.dev`,
  [Environment.QA1]: `https://prostudio.qa1.pypestream.dev`,
  [Environment.QA2]: `https://prostudio.qa2.pypestream.dev`,
};

export const getEnvironmentFromUrl = (url: string): Environment | null => {
  return (
    (Object.entries(baseUrls).find(([, baseUrl]) =>
      url.startsWith(baseUrl)
    )?.[0] as Environment) || null
  );
};

export const getLauncherUrls = (origin: string) => {
  const env = getEnvironmentFromUrl(origin) || Environment.Main;

  const baseUrl = env === Environment.Production ? '' : `${env}.`;
  const topLevelDomain = env === Environment.Production ? 'com' : 'dev';

  return {
    live: `https://web.${baseUrl}pypestream.${topLevelDomain}`,
    sandbox: `https://web-sandbox.${baseUrl}pypestream.${topLevelDomain}`,
  };
};

/**
 * The real, running solution for a channel — the launcher's `preview.html`
 * harness booted against that channel's app id. This is the "live solution"
 * link, NOT the static mock rendered by `<PypePreview/>` inside the settings
 * form: opening it starts an actual conversation with the deployed channel.
 *
 * Shared by the channels-list row action and the Channel Settings "Open Real
 * Preview" block so the two entry points cannot drift apart (PSTUDIO-3768).
 */
export const buildLivePreviewUrl = (
  origin: string,
  env: 'live' | 'sandbox',
  channelId: string
) => `${getLauncherUrls(origin)[env]}/preview.html?id=${channelId}`;

/**
 * Pype Next (pypenext) origin for the current environment AND stack.
 *
 * Mirrors `buildEngagementApiUrl` — pypenext is deployed as two stacks per env
 * (live + sandbox), and each stack advertises its own loader URL, bakes in its
 * own engagement API, and serves its own preview harness. So a sandbox channel
 * must be pointed at the sandbox stack or its harness talks to the wrong
 * backend. The `-sandbox` suffix is applied exactly as the engagement-api URL
 * does it.
 *
 * e.g. production live    -> https://pypenext.pypestream.com
 *      production sandbox -> https://pypenext-sandbox.pypestream.com
 *      main sandbox       -> https://pypenext-sandbox.main.pypestream.dev
 * Localhost/unknown origins fall back to Main.
 */
export const getPypenextBaseUrl = (
  origin: string,
  channelEnv: 'live' | 'sandbox'
): string => {
  const env = getEnvironmentFromUrl(origin) ?? Environment.Main;
  const sandboxSuffix = channelEnv === 'sandbox' ? '-sandbox' : '';

  switch (env) {
    case Environment.Production:
      return `https://pypenext${sandboxSuffix}.pypestream.com`;
    case Environment.EU:
      return `https://pypenext${sandboxSuffix}.pypestream.eu`;
    case Environment.Candidate:
    case Environment.UAT:
    case Environment.Main:
    case Environment.QA1:
    case Environment.QA2:
      return `https://pypenext${sandboxSuffix}.${env}.pypestream.dev`;
    default:
      return `https://pypenext${sandboxSuffix}.main.pypestream.dev`;
  }
};

/** Pype Next docs site for the channel's environment + stack. */
export const getPypenextDocsUrl = (
  origin: string,
  channelEnv: 'live' | 'sandbox'
): string => `${getPypenextBaseUrl(origin, channelEnv)}/docs`;

/**
 * The Pype Next preview harness, pre-seeded with the channel's app id.
 *
 * The harness reads `?appId` (URL wins over its own localStorage) and boots on
 * load, so this link starts a real conversation with THIS channel without the
 * user pasting anything. Distinct from `buildLivePreviewUrl`, which is the
 * legacy (Pype 1.0) launcher harness.
 */
export const getPypenextHarnessUrl = (
  origin: string,
  channelEnv: 'live' | 'sandbox',
  appId: string
): string =>
  `${getPypenextBaseUrl(origin, channelEnv)}/preview/harness.html?appId=${encodeURIComponent(appId)}`;

/**
 * The loader script a customer imports from. Goes into the embed snippet, so it
 * must be the moving per-env alias — never a per-PR pinned origin.
 */
export const getPypeNextLoaderUrl = (
  origin: string,
  channelEnv: 'live' | 'sandbox'
): string => `${getPypenextBaseUrl(origin, channelEnv)}/loader.js`;
